import { useState, useEffect } from 'react';
import { Usd, PriceCaption } from '@/utils/CurrencyContext';
import { useNavigate } from 'react-router-dom';
import { CourseIconPicker } from '@/components/CourseIconPicker';
import { CourseVisual } from '@/components/CourseVisual';
import { TeacherSignatureCard } from '@/components/teacher/TeacherSignatureCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ButtonWithTooltip } from '@/components/ui/button-with-tooltip';
import api from '@/utils/api';
import { toast } from 'sonner';
import { BookOpen, Users, DollarSign, Plus, Edit, Trash2, ArrowLeft, CreditCard, Mail, User, Package, Bell, Check, Video, Calendar, Clock, Loader2, Megaphone, Image } from 'lucide-react';
import { motion } from 'framer-motion';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';

const TeacherDashboard = () => {
  const [courses, setCourses] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateCourse, setShowCreateCourse] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [includeKit, setIncludeKit] = useState(false);
  const [showKitDialog, setShowKitDialog] = useState(false);
  const [kitItems, setKitItems] = useState([{ name: '', quantity: 1, description: '', unit_price: 0 }]);
  const [pendingCourseData, setPendingCourseData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [liveSessions, setLiveSessions] = useState([]);
  const [showScheduleSession, setShowScheduleSession] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [sessionForm, setSessionForm] = useState({
    course_id: '',
    title: '',
    scheduled_at: '',
    duration: 60
  });
  const navigate = useNavigate();

  // Calculate total kit price
  const totalKitPrice = kitItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

  useEffect(() => {
    fetchData();
    fetchUnreadCount();
    fetchNotifications();
    fetchLiveSessions();
    
    // Periodic refresh for notifications (every 30 seconds)
    const id = setInterval(fetchNotifications, 30000);
    return () => clearInterval(id);
  }, []);

  const fetchLiveSessions = async () => {
    try {
      const response = await api.get('/live-sessions/upcoming');
      setLiveSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch live sessions:', error);
    }
  };

  const scheduleSession = async (e) => {
    e.preventDefault();
    if (!sessionForm.course_id || !sessionForm.title || !sessionForm.scheduled_at) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsScheduling(true);
    try {
      await api.post('/live-sessions', {
        ...sessionForm,
        scheduled_at: new Date(sessionForm.scheduled_at).toISOString()
      });
      toast.success('Live session scheduled! Students will be notified.');
      setShowScheduleSession(false);
      setSessionForm({ course_id: '', title: '', scheduled_at: '', duration: 60 });
      fetchLiveSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule session');
    } finally {
      setIsScheduling(false);
    }
  };

  const deleteSession = async (sessionId) => {
    if (!confirm('Delete this scheduled session?')) return;
    try {
      await api.delete(`/live-sessions/${sessionId}`);
      toast.success('Session deleted');
      fetchLiveSessions();
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get('/chat/unread-count');
      setUnreadMessages(response.data.unread_count);
    } catch (error) {
      // Ignore error
    }
  };

  const fetchNotifications = async () => {
    try {
      const [notificationsRes, unreadCountRes] = await Promise.all([
        api.get('/notifications'),
        api.get('/notifications/unread-count')
      ]);
      setNotifications(notificationsRes.data);
      setUnreadNotifications(unreadCountRes.data.unread_count);
    } catch (error) {
      // Ignore error
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (error) {
      // Ignore error
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await api.put('/notifications/mark-all-read');
      fetchNotifications();
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark notifications as read');
    }
  };

  const fetchData = async () => {
    try {
      const [coursesRes, analyticsRes] = await Promise.all([
        api.get('/courses/my'),
        api.get('/analytics/teacher'),
      ]);
      
      // Sort courses: first created course appears first (oldest to newest)
      const sortedCourses = [...coursesRes.data].sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateA - dateB; // Ascending order (oldest first)
      });
      
      setCourses(sortedCourses);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    // If kit is included, show kit dialog first
    if (includeKit) {
      setPendingCourseData({
        title: formData.get('title'),
        description: formData.get('description'),
        price: parseFloat(formData.get('price')),
        video_intro_url: formData.get('video_intro_url') || null,
        video_intro_type: 'youtube',
        has_kit: true
      });
      setShowCreateCourse(false);
      setShowKitDialog(true);
      return;
    }
    
    setIsCreating(true);
    
    try {
      const response = await api.post('/courses', {
        title: formData.get('title'),
        description: formData.get('description'),
        price: parseFloat(formData.get('price')),
        video_intro_url: formData.get('video_intro_url') || null,
        video_intro_type: 'youtube',
        has_kit: false
      });
      
      toast.success('Course created successfully!');
      setShowCreateCourse(false);
      setCourses([response.data, ...courses]);
      setIncludeKit(false);
      e.target.reset();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create course');
    } finally {
      setIsCreating(false);
    }
  };

  const handleSaveKitAndCreateCourse = async () => {
    if (!pendingCourseData) return;
    
    // Validate kit items
    const validItems = kitItems.filter(item => item.name.trim());
    if (validItems.length === 0) {
      toast.error('Please add at least one kit item');
      return;
    }
    
    setIsCreating(true);
    
    try {
      // Create the course first
      const response = await api.post('/courses', pendingCourseData);
      const courseId = response.data.id;
      
      // Then save the kit items
      await api.post(`/admin/courses/${courseId}/kit`, {
        items: validItems.map(item => ({
          name: item.name.trim(),
          quantity: parseInt(item.quantity) || 1,
          description: item.description?.trim() || null
        }))
      });
      
      toast.success('Course created with kit items! Admin has been notified.');
      setShowKitDialog(false);
      setCourses([response.data, ...courses]);
      setPendingCourseData(null);
      setKitItems([{ name: '', quantity: 1, description: '', unit_price: 0 }]);
      setIncludeKit(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create course with kit');
    } finally {
      setIsCreating(false);
    }
  };

  const addKitItem = () => {
    setKitItems([...kitItems, { name: '', quantity: 1, description: '', unit_price: 0 }]);
  };

  const removeKitItem = (index) => {
    if (kitItems.length > 1) {
      setKitItems(kitItems.filter((_, i) => i !== index));
    }
  };

  const updateKitItem = (index, field, value) => {
    const updated = [...kitItems];
    updated[index][field] = value;
    setKitItems(updated);
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    
    try {
      await api.delete(`/courses/${courseId}`);
      toast.success('Course deleted successfully');
      setCourses(courses.filter(c => c.id !== courseId));
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you absolutely sure? This will permanently delete your account and all your courses. This action cannot be undone!')) return;
    
    setIsDeleting(true);
    try {
      await api.delete('/auth/delete-account');
      toast.success('Account deleted successfully');
      localStorage.clear();
      navigate('/');
    } catch (error) {
      toast.error('Failed to delete account');
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#163329' }}></div>
          <p style={{ color: '#5C635A' }}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-6" style={{ backgroundColor: '#FAF9F6' }} data-testid="teacher-dashboard">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-4"
          data-testid="back-to-home-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>
        <div className="mb-8 flex flex-col sm:flex-row justify-between items-start gap-4">
          <div>
            <h1
              className="text-2xl sm:text-4xl font-bold tracking-tight mb-2"
              style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
            >
              Teacher Dashboard
            </h1>
            <p className="text-sm sm:text-base" style={{ color: '#5C635A' }}>Manage your courses and track student progress</p>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <ButtonWithTooltip title="My Profile" description="View and edit your profile information">
              <Button
                onClick={() => navigate('/teacher/profile')}
                className="rounded-full text-xs sm:text-sm"
                variant="outline"
                style={{ borderColor: '#163329', color: '#163329' }}
                data-testid="profile-button"
                aria-label="My Profile: View and edit your profile information"
              >
                <User className="h-4 w-4 mr-1.5" />
                <span>My Profile</span>
              </Button>
            </ButtonWithTooltip>
            
            {/* Notifications Bell */}
            <Dialog open={showNotifications} onOpenChange={setShowNotifications}>
              <ButtonWithTooltip title="Notifications" description="View your notifications and alerts">
                <DialogTrigger asChild>
                  <Button
                    className="rounded-full relative text-xs sm:text-sm"
                    variant="outline"
                    style={{ borderColor: '#163329', color: '#163329' }}
                    data-testid="notifications-button"
                    aria-label="Notifications: View your notifications and alerts"
                  >
                    <Bell className="h-4 w-4 mr-1.5" />
                    <span>Alerts</span>
                    {unreadNotifications > 0 && (
                      <span 
                        className="absolute -top-2 -right-2 h-5 w-5 rounded-full flex items-center justify-center text-xs text-white"
                        style={{ backgroundColor: '#D86B47' }}
                      >
                        {unreadNotifications}
                      </span>
                    )}
                  </Button>
                </DialogTrigger>
              </ButtonWithTooltip>
              <DialogContent className="max-w-md max-h-[70vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center justify-between" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    <span><Bell className="inline h-5 w-5 mr-2" />Notifications</span>
                    {unreadNotifications > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={markAllNotificationsRead}
                        className="text-sm"
                        style={{ color: '#D86B47' }}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Mark all read
                      </Button>
                    )}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 pt-4">
                  {notifications.length === 0 ? (
                    <p className="text-center py-8" style={{ color: '#5C635A' }}>No notifications yet</p>
                  ) : (
                    notifications.map((notif) => (
                      <div 
                        key={notif.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${notif.read ? 'opacity-60' : ''}`}
                        style={{ 
                          backgroundColor: notif.read ? '#FFFFFF' : '#FFF8E1',
                          borderColor: notif.read ? '#E3E1DB' : '#FFA000'
                        }}
                        onClick={() => !notif.read && markNotificationRead(notif.id)}
                      >
                        <div className="flex justify-between items-start">
                          <h4 className="font-medium text-sm" style={{ color: '#163329' }}>{notif.title}</h4>
                          {!notif.read && (
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: '#D86B47' }}></span>
                          )}
                        </div>
                        <p className="text-sm mt-1" style={{ color: '#5C635A' }}>{notif.message}</p>
                        <p className="text-xs mt-2" style={{ color: '#888' }}>
                          {new Date(notif.created_at).toLocaleDateString()} at {new Date(notif.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </DialogContent>
            </Dialog>
            
            <ButtonWithTooltip title="Messages" description="Chat with students and other teachers">
              <Button
                onClick={() => navigate('/chat')}
                className="rounded-full relative text-xs sm:text-sm"
                variant="outline"
                style={{ borderColor: '#163329', color: '#163329' }}
                data-testid="messages-button"
                aria-label="Messages: Chat with students and other teachers"
              >
                <Mail className="h-4 w-4 mr-1.5" />
                <span>Messages</span>
                {unreadMessages > 0 && (
                  <span 
                    className="absolute -top-2 -right-2 h-5 w-5 rounded-full flex items-center justify-center text-xs text-white"
                    style={{ backgroundColor: '#D86B47' }}
                  >
                    {unreadMessages}
                  </span>
                )}
              </Button>
            </ButtonWithTooltip>
            <ButtonWithTooltip title="Payment Status" description="Manage your payout details and earnings">
              <Button
                onClick={() => navigate('/teacher/payment-settings')}
                variant="outline"
                className="rounded-full text-xs sm:text-sm"
                style={{ borderColor: '#163329', color: '#163329' }}
                data-testid="payment-settings-button"
                aria-label="Payment Status: Manage your payout details and earnings"
              >
                <CreditCard className="h-4 w-4 mr-1.5" />
                <span>Payments</span>
              </Button>
            </ButtonWithTooltip>
            <ButtonWithTooltip title="My Products" description="Manage your marketplace products">
              <Button
                onClick={() => navigate('/teacher/products')}
                variant="outline"
                className="rounded-full text-xs sm:text-sm"
                style={{ borderColor: '#163329', color: '#163329' }}
                data-testid="my-products-button"
                aria-label="My Products: Manage your marketplace products"
              >
                <Package className="h-4 w-4 mr-1.5" />
                <span>Products</span>
              </Button>
            </ButtonWithTooltip>
            <ButtonWithTooltip title="Announcements" description="Create and manage your announcements">
              <Button
                onClick={() => navigate('/teacher/announcements')}
                variant="outline"
                className="rounded-full text-xs sm:text-sm"
                style={{ borderColor: '#163329', color: '#163329' }}
                data-testid="my-announcements-button"
                aria-label="Announcements: Create and manage your announcements"
              >
                <Megaphone className="h-4 w-4 mr-1.5" />
                <span>Announce</span>
              </Button>
            </ButtonWithTooltip>
            <ButtonWithTooltip title="My Gallery" description="Showcase your work with images">
              <Button
                onClick={() => navigate('/teacher/gallery')}
                variant="outline"
                className="rounded-full text-xs sm:text-sm"
                style={{ borderColor: '#163329', color: '#163329' }}
                data-testid="my-gallery-button"
                aria-label="My Gallery: Showcase your work with images"
              >
                <Image className="h-4 w-4 mr-1.5" />
                <span>Gallery</span>
              </Button>
            </ButtonWithTooltip>
            <Button
              onClick={handleDeleteAccount}
              className="rounded-full text-xs sm:text-sm"
              variant="destructive"
              disabled={isDeleting}
              data-testid="delete-account-button"
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              <span>{isDeleting ? '...' : 'Delete'}</span>
            </Button>
            <Dialog open={showCreateCourse} onOpenChange={setShowCreateCourse}>
              <DialogTrigger asChild>
                <Button
                  className="rounded-full"
                  
                  data-testid="create-course-button"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create Course
                </Button>
              </DialogTrigger>
            <DialogContent data-testid="create-course-modal">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Spectral, serif' }}>Create New Course</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateCourse} className="space-y-4">
                <div>
                  <Label htmlFor="title">Course Title</Label>
                  <Input id="title" name="title" required data-testid="course-title-input" />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea id="description" name="description" rows={3} required data-testid="course-description-input" />
                </div>
                <div>
                  <Label htmlFor="price">Price (USD)</Label>
                  <Input id="price" name="price" type="number" step="0.01" min="0" required data-testid="course-price-input" />
                </div>
                <div>
                  <Label htmlFor="video_intro_url">Intro Video URL (YouTube, optional)</Label>
                  <Input id="video_intro_url" name="video_intro_url" type="url" placeholder="https://youtube.com/watch?v=..." data-testid="course-video-input" />
                </div>
                
                {/* Kit Checkbox */}
                <div className="flex items-start space-x-3 p-4 rounded-lg" style={{ backgroundColor: '#F0EFEA', border: '1px solid #E3E1DB' }}>
                  <Checkbox
                    id="include-kit"
                    checked={includeKit}
                    onCheckedChange={setIncludeKit}
                    className="mt-1"
                    data-testid="include-kit-checkbox"
                  />
                  <div className="flex-1">
                    <Label htmlFor="include-kit" className="font-medium cursor-pointer" style={{ color: '#163329' }}>
                      <Package className="inline h-4 w-4 mr-2" />
                      This course includes a learning kit
                    </Label>
                    <p className="text-xs mt-1" style={{ color: '#5C635A' }}>
                      Check this if your course requires physical materials. You'll be asked to list the items needed. 
                      Insight Evolution will supply kits on your behalf. Teachers, Mentors, Skill Creators are encouraged to include kit as home learning Insight Evolution requirements and mission. Teachers must include their appropriate, affordable price then kit will be verified in 2 to 5 days and the price will automatically adjusted by the admin of Insight Evolution with notification to proceed with tutorials.
                    </p>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={isCreating}
                  className="w-full"
                  
                  data-testid="submit-create-course"
                >
                  {isCreating ? 'Creating...' : includeKit ? 'Continue to Kit Items' : 'Create Course'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          
          {/* Kit Items Dialog */}
          <Dialog open={showKitDialog} onOpenChange={(open) => {
            if (!open) {
              setPendingCourseData(null);
              setKitItems([{ name: '', quantity: 1, description: '', unit_price: 0 }]);
            }
            setShowKitDialog(open);
          }}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="kit-items-modal">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                  <Package className="inline h-5 w-5 mr-2" />
                  List Kit Items for Your Course
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#E3F2FD', border: '1px solid #90CAF9' }}>
                  <p className="text-sm" style={{ color: '#1565C0' }}>
                    List all materials/items students will need to complete this course. 
                    Insight Evolution will manufacture, arrange and supply these kits on your behalf.
                    Delivery typically takes 3-6 weeks.
                  </p>
                </div>
                
                <div className="space-y-3">
                  {kitItems.map((item, index) => (
                    <div key={index} className="flex gap-2 items-start p-3 rounded-lg" style={{ backgroundColor: '#F0EFEA' }}>
                      <div className="flex-1 space-y-2">
                        <Input
                          placeholder="Item name (e.g., Paint brush set)"
                          value={item.name}
                          onChange={(e) => updateKitItem(index, 'name', e.target.value)}
                          data-testid={`kit-item-name-${index}`}
                        />
                        <div className="flex gap-2">
                          <div className="w-20">
                            <Label className="text-xs" style={{ color: '#5C635A' }}>Qty</Label>
                            <Input
                              type="number"
                              min="1"
                              placeholder="Qty"
                              value={item.quantity}
                              onChange={(e) => updateKitItem(index, 'quantity', parseInt(e.target.value) || 1)}
                              data-testid={`kit-item-qty-${index}`}
                            />
                          </div>
                          <div className="w-28">
                            <Label className="text-xs" style={{ color: '#5C635A' }}>Unit Price ($)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={item.unit_price || ''}
                              onChange={(e) => updateKitItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                              data-testid={`kit-item-price-${index}`}
                            />
                          </div>
                          <div className="flex-1">
                            <Label className="text-xs" style={{ color: '#5C635A' }}>Description</Label>
                            <Input
                              placeholder="Optional description"
                              value={item.description}
                              onChange={(e) => updateKitItem(index, 'description', e.target.value)}
                              data-testid={`kit-item-desc-${index}`}
                            />
                          </div>
                        </div>
                        <div className="text-right text-sm font-medium" style={{ color: '#163329' }}>
                          Item Total: <Usd value={((item.quantity || 0) * (item.unit_price || 0))} />
                        </div>
                      </div>
                      {kitItems.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeKitItem(index)}
                          className="text-red-500 hover:text-red-700 mt-6"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Kit Total Price Summary */}
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#163329' }}>
                  <div className="flex justify-between items-center text-white">
                    <span className="font-medium">Total Kit Price:</span>
                    <span className="text-xl font-bold" style={{ color: '#D86B47' }}><Usd value={totalKitPrice} /></span>
                  </div>
                  <p className="text-xs mt-2" style={{ color: '#E3E1DB' }}>
                    This amount will be added to your course base price. Final price may be adjusted by admin after kit verification.
                  </p>
                </div>
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={addKitItem}
                  className="w-full"
                  data-testid="add-kit-item-button"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Another Item
                </Button>
                
                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowKitDialog(false);
                      setShowCreateCourse(true);
                    }}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleSaveKitAndCreateCourse}
                    disabled={isCreating}
                    className="flex-1"
                    
                    data-testid="save-kit-create-course"
                  >
                    {isCreating ? 'Creating...' : 'Create Course with Kit'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Stats Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
              <Card style={{ borderColor: '#E3E1DB' }} data-testid="total-courses-stat">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium" style={{ color: '#5C635A' }}>Total Courses</CardTitle>
                  <BookOpen className="h-4 w-4" style={{ color: '#163329' }} />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    {analytics.total_courses}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
              <Card style={{ borderColor: '#E3E1DB' }} data-testid="total-students-stat">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium" style={{ color: '#5C635A' }}>Total Students</CardTitle>
                  <Users className="h-4 w-4" style={{ color: '#D86B47' }} />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    {analytics.total_students}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
              <Card style={{ borderColor: '#E3E1DB' }} data-testid="teacher-earnings-stat">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium" style={{ color: '#5C635A' }}>Teacher's Earnings</CardTitle>
                  <DollarSign className="h-4 w-4" style={{ color: '#E2A761' }} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    <Usd value={(analytics.teacher_earnings || 0)} />
                  </div>
                  <PriceCaption />
                </CardContent>
              </Card>
            </motion.div>
          </div>
        )}

        {/* Live Sessions Section */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
              <Video className="inline h-6 w-6 mr-2" />
              Live Sessions
            </h2>
            <Dialog open={showScheduleSession} onOpenChange={setShowScheduleSession}>
              <DialogTrigger asChild>
                <Button
                  className="rounded-full"
                  style={{ backgroundColor: '#163329' }}
                  data-testid="schedule-session-btn"
                  disabled={courses.length === 0}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Schedule Session
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                    <Video className="inline h-5 w-5 mr-2" />
                    Schedule Live Session
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={scheduleSession} className="space-y-4 pt-4">
                  <div>
                    <Label htmlFor="session-course">Course *</Label>
                    <Select
                      value={sessionForm.course_id}
                      onValueChange={(value) => setSessionForm({ ...sessionForm, course_id: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select a course" />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map((course) => (
                          <SelectItem key={course.id} value={course.id}>
                            {course.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="session-title">Session Title *</Label>
                    <Input
                      id="session-title"
                      value={sessionForm.title}
                      onChange={(e) => setSessionForm({ ...sessionForm, title: e.target.value })}
                      placeholder="e.g., Introduction to Techniques"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="session-datetime">Date & Time *</Label>
                    <Input
                      id="session-datetime"
                      type="datetime-local"
                      value={sessionForm.scheduled_at}
                      onChange={(e) => setSessionForm({ ...sessionForm, scheduled_at: e.target.value })}
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="session-duration">Duration (minutes)</Label>
                    <Input
                      id="session-duration"
                      type="number"
                      min="15"
                      max="240"
                      value={sessionForm.duration}
                      onChange={(e) => setSessionForm({ ...sessionForm, duration: parseInt(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full rounded-full"
                    style={{ backgroundColor: '#163329' }}
                    disabled={isScheduling}
                  >
                    {isScheduling ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Scheduling...
                      </>
                    ) : (
                      <>
                        <Calendar className="h-4 w-4 mr-2" />
                        Schedule Session
                      </>
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {liveSessions.length === 0 ? (
            <Card className="p-8 text-center" style={{ borderColor: '#E3E1DB' }}>
              <Video className="h-12 w-12 mx-auto mb-4" style={{ color: '#E3E1DB' }} />
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#163329' }}>No upcoming sessions</h3>
              <p className="text-sm" style={{ color: '#5C635A' }}>
                Schedule a live video session to interact with your students in real-time.
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {liveSessions.map((session) => (
                <Card key={session.id} className="p-4" style={{ borderColor: '#E3E1DB' }}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold" style={{ color: '#163329' }}>{session.title}</h4>
                        <Badge 
                          className={
                            session.status === 'live' ? 'bg-red-500 animate-pulse' :
                            session.status === 'ended' ? 'bg-gray-500' : 'bg-blue-500'
                          }
                        >
                          {session.status === 'live' ? 'LIVE' : session.status === 'ended' ? 'Ended' : 'Scheduled'}
                        </Badge>
                      </div>
                      <p className="text-sm mb-2" style={{ color: '#5C635A' }}>{session.course_title}</p>
                      <div className="flex items-center gap-4 text-xs" style={{ color: '#5C635A' }}>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(session.scheduled_at).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(session.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span>{session.duration} min</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      {session.status !== 'ended' && (
                        <Button
                          size="sm"
                          className="rounded-full"
                          style={{ backgroundColor: session.status === 'live' ? '#D86B47' : '#163329' }}
                          onClick={() => navigate(`/live-session/${session.id}`)}
                        >
                          <Video className="h-4 w-4 mr-1" />
                          {session.status === 'live' ? 'Join' : 'Start'}
                        </Button>
                      )}
                      {session.status === 'ended' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          style={{ borderColor: '#163329', color: '#163329' }}
                          onClick={() => navigate(`/live-session/${session.id}`)}
                        >
                          <Users className="h-4 w-4 mr-1" />
                          View Attendance
                        </Button>
                      )}
                      {session.status === 'scheduled' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full text-red-500 border-red-500"
                          onClick={() => deleteSession(session.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        <TeacherSignatureCard />

        {/* My Courses */}
        <div>
          <h2 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
            My Courses
          </h2>

          {courses.length === 0 ? (
            <Card className="p-12 text-center" style={{ borderColor: '#E3E1DB' }}>
              <BookOpen className="h-12 w-12 mx-auto mb-4" style={{ color: '#5C635A' }} />
              <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                No courses yet
              </h3>
              <p className="mb-4" style={{ color: '#5C635A' }}>Create your first course to start teaching</p>
              <Button
                onClick={() => setShowCreateCourse(true)}
                
                className="rounded-full"
                data-testid="create-first-course-button"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create Your First Course
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {courses.map((course, index) => (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card
                    className="h-full border"
                    style={{ borderColor: '#E3E1DB' }}
                    data-testid={`teacher-course-${course.id}`}
                  >
                    <CourseVisual course={course} className="aspect-video rounded-t-lg" iconClassName="h-10 w-10" />
                    <CardHeader>
                      <CardTitle
                        className="text-lg line-clamp-2"
                        style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
                      >
                        {course.title}
                      </CardTitle>
                      <div className="pt-2">
                        <CourseIconPicker
                          courseId={course.id}
                          value={course.icon}
                          imageUrl={course.thumbnail_url}
                          size="sm"
                          onChange={(icon) => setCourses((prev) => prev.map((c) => (c.id === course.id ? { ...c, icon } : c)))}
                          onImageChange={(thumbnail_url) => setCourses((prev) => prev.map((c) => (c.id === course.id ? { ...c, thumbnail_url } : c)))}
                        />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span style={{ color: '#5C635A' }}>Students</span>
                          <span className="font-medium" style={{ color: '#163329' }}>{course.enrollment_count || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span style={{ color: '#5C635A' }}>Lessons</span>
                          <span className="font-medium" style={{ color: '#163329' }}>{course.lesson_count || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span style={{ color: '#5C635A' }}>Price</span>
                          <span className="font-medium" style={{ color: '#163329' }}><Usd value={course.price} /></span>
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => navigate(`/teacher/courses/${course.id}/manage`)}
                            data-testid={`manage-course-${course.id}`}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Manage
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteCourse(course.id)}
                            data-testid={`delete-course-${course.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherDashboard;